import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { DashboardSidebarVariablesWidget } from './DashboardSidebarVariablesWidget';
import { DashboardWidgetSidebarItem } from './models';
import {
  DataSourceSettingsDto,
  DashboardDto,
  DashboardMutatePayload,
  MutateWidgetPayload,
  MutateVariablePayload,
  VariableDto,
  VariableSourceSettingsDto,
  WidgetDto,
} from '@thingslog/repositories';
import { DashboardSidebarWidgetCard } from './DashboardSidebarWidgetCard';
import { DashboardSidebarVariableEditWidget } from './DashboardSidebarVariableEditWidget';
import { DashboardSidebarVariableAddWidget } from './DashboardSidebarVariableAddWidget';
import { DashboardSidebarDashboardWidgetAddWidget } from './DashboardSidebarDashboardWidgetAddWidget';
import { DashboardSidebarDashboardWidgetEditWidget } from './DashboardSidebarDashboardWidgetEditWidget';
import { DashboardSidebarDashboardAddWidget } from './DashboardSidebarDashboardAddWidget';
import { DashboardSidebarDashboardEditWidget } from './DashboardSidebarDashboardEditWidget';
import { DashboardSidebarDashboardTimeframeWidget } from './DashboardSidebarDashboardTimeframeWidget';
import { DashboardSidebarDashboardWidgetsWidget } from './DashboardSidebarDashboardWidgetsWidget';
import { DashboardSidebarDashboardWidgetTimeframeWidget } from './DashboardSidebarDashboardWidgetTimeframeWidget';

interface MutationCallbacks {
  onSuccess?: () => void;
}

interface Props {
  widget: DashboardWidgetSidebarItem;
  dashboard: DashboardDto | null;
  variables: VariableDto[];
  variableSources: VariableSourceSettingsDto[];
  dashboardWidgets: WidgetDto[];
  dataSources: DataSourceSettingsDto[];
  companyId?: number | null;
  onAddVariable: () => void;
  onEditVariable: (variable: VariableDto) => void;
  onDeleteVariable: (variable: VariableDto, callbacks?: MutationCallbacks) => void;
  onPersistVariable: (
    id: string,
    payload: MutateVariablePayload,
    callbacks?: MutationCallbacks
  ) => void;
  onTempSaveVariable: (id: string, payload: MutateVariablePayload) => void;
  onPersistNewVariable: (payload: MutateVariablePayload, callbacks?: MutationCallbacks) => void;
  isPersistingVariable: boolean;
  isPersistingNewVariable: boolean;
  isDeletingVariable: boolean;
  onPersistDashboard: (
    dashboardId: string,
    payload: DashboardMutatePayload,
    callbacks?: MutationCallbacks
  ) => void;
  onPersistNewDashboard: (payload: DashboardMutatePayload, callbacks?: MutationCallbacks) => void;
  isPersistingDashboard: boolean;
  isPersistingNewDashboard: boolean;
  onAddDashboardWidget: () => void;
  onEditDashboardWidget: (dashboardWidget: WidgetDto) => void;
  onPersistWidget: (
    widgetId: string,
    payload: MutateWidgetPayload,
    callbacks?: MutationCallbacks
  ) => void;
  onPersistNewWidget: (payload: MutateWidgetPayload, callbacks?: MutationCallbacks) => void;
  onDeleteWidget: (widget: WidgetDto, callbacks?: MutationCallbacks) => void;
  isPersistingWidget: boolean;
  isPersistingNewWidget: boolean;
  isDeletingWidget: boolean;
  onClose: (widgetId: string) => void;
  onStartWidgetTour: (widgetId: string) => void;
  isMobileRootWidget?: (widget: DashboardWidgetSidebarItem) => boolean;
}

export const DashboardSidebarWidget: FC<Props> = ({
  widget,
  dashboard,
  variables,
  variableSources,
  dashboardWidgets,
  dataSources,
  companyId,
  onAddVariable,
  onEditVariable,
  onDeleteVariable,
  onPersistVariable,
  onTempSaveVariable,
  onPersistNewVariable,
  isPersistingVariable,
  isPersistingNewVariable,
  isDeletingVariable,
  onPersistDashboard,
  onPersistNewDashboard,
  isPersistingDashboard,
  isPersistingNewDashboard,
  onAddDashboardWidget,
  onEditDashboardWidget,
  onPersistWidget,
  onPersistNewWidget,
  onDeleteWidget,
  isPersistingWidget,
  isPersistingNewWidget,
  isDeletingWidget,
  onClose,
  onStartWidgetTour,
  isMobileRootWidget,
}: Props) => {
  const { t } = useTranslation();
  const editedVariable =
    widget.type === 'variable-edit'
      ? variables.find((variable: VariableDto): boolean => variable.id === widget.variableId) ??
        null
      : null;
  const editedDashboardWidget =
    widget.type === 'dashboard-widget-edit' || widget.type === 'dashboard-widget-timeframe-edit'
      ? dashboardWidgets.find(
          (dashboardWidget: WidgetDto): boolean => dashboardWidget.id === widget.dashboardWidgetId
        ) ?? null
      : null;
  const handleStartTour = (): void => {
    onStartWidgetTour(widget.id);
  };
  const isRootWidgetLocked = isMobileRootWidget?.(widget) ?? false;

  switch (widget.type) {
    case 'variables':
      return (
        <DashboardSidebarWidgetCard
          title={t('dashboard_variables', 'Variables')}
          widgetId={widget.id}
          closable={!isRootWidgetLocked}
          onClose={onClose}
          onStartTour={handleStartTour}
        >
          <DashboardSidebarVariablesWidget
            widgetId={widget.id}
            variables={variables}
            isDeletingVariable={isDeletingVariable}
            onAddVariable={onAddVariable}
            onEditVariable={onEditVariable}
            onDeleteVariable={onDeleteVariable}
          />
        </DashboardSidebarWidgetCard>
      );
    case 'variable-edit':
      return (
        <DashboardSidebarVariableEditWidget
          widgetId={widget.id}
          variable={editedVariable}
          variableSources={variableSources}
          companyId={companyId}
          isPersisting={isPersistingVariable}
          onPersistVariable={onPersistVariable}
          onTempSaveVariable={onTempSaveVariable}
          onClose={onClose}
          onStartTour={handleStartTour}
        />
      );
    case 'variable-add':
      return (
        <DashboardSidebarVariableAddWidget
          widgetId={widget.id}
          variableSources={variableSources}
          companyId={companyId}
          isPersisting={isPersistingNewVariable}
          onPersistNewVariable={onPersistNewVariable}
          onClose={onClose}
          onStartTour={handleStartTour}
        />
      );
    case 'dashboard-add':
      return (
        <DashboardSidebarDashboardAddWidget
          widgetId={widget.id}
          isPersisting={isPersistingNewDashboard}
          onPersistNewDashboard={onPersistNewDashboard}
          onClose={onClose}
          onStartTour={handleStartTour}
        />
      );
    case 'dashboard-edit':
      return (
        <DashboardSidebarDashboardEditWidget
          widgetId={widget.id}
          dashboard={dashboard}
          isPersisting={isPersistingDashboard}
          onPersistDashboard={onPersistDashboard}
          onClose={onClose}
          onStartTour={handleStartTour}
        />
      );
    case 'dashboard-timeframe-edit':
      return (
        <DashboardSidebarDashboardTimeframeWidget
          widgetId={widget.id}
          dashboard={dashboard}
          isPersisting={isPersistingDashboard}
          onPersistDashboard={onPersistDashboard}
          onClose={onClose}
          onStartTour={handleStartTour}
        />
      );
    case 'dashboard-widgets':
      return (
        <DashboardSidebarDashboardWidgetsWidget
          widgetId={widget.id}
          dashboardWidgets={dashboardWidgets}
          closable={!isRootWidgetLocked}
          isDeletingWidget={isDeletingWidget}
          onAddWidget={onAddDashboardWidget}
          onEditWidget={onEditDashboardWidget}
          onDeleteWidget={onDeleteWidget}
          onClose={onClose}
          onStartTour={handleStartTour}
        />
      );
    case 'dashboard-widget-add':
      return (
        <DashboardSidebarDashboardWidgetAddWidget
          widgetId={widget.id}
          dataSources={dataSources}
          variables={variables}
          isPersisting={isPersistingNewWidget}
          onPersistNewWidget={onPersistNewWidget}
          onClose={onClose}
          onStartTour={handleStartTour}
        />
      );
    case 'dashboard-widget-edit':
      return (
        <DashboardSidebarDashboardWidgetEditWidget
          widgetId={widget.id}
          dashboardWidget={editedDashboardWidget}
          dataSources={dataSources}
          variables={variables}
          isPersisting={isPersistingWidget}
          isDeleting={isDeletingWidget}
          onPersistWidget={onPersistWidget}
          onDeleteWidget={onDeleteWidget}
          onClose={onClose}
          onStartTour={handleStartTour}
        />
      );
    case 'dashboard-widget-timeframe-edit':
      return (
        <DashboardSidebarDashboardWidgetTimeframeWidget
          widgetId={widget.id}
          dashboardWidget={editedDashboardWidget}
          isPersisting={isPersistingWidget}
          onPersistWidget={onPersistWidget}
          onClose={onClose}
          onStartTour={handleStartTour}
        />
      );
    default:
      return null;
  }
};
